import React, { useState } from 'react';
import { AlertCircle, CheckCircle, Gift, Zap, X, FileText } from 'lucide-react';
import PageIdentifier from '../shared/PageIdentifier';
import SCREEN_IDS from '../../utils/screenIds';
import usePermissions from '../../hooks/usePermissions';
import { todayCentral } from '../../shared/utils/dateUtils';

const SchemeRedemption = () => {
  const { hasPermission } = usePermissions();
  const canRedeem = hasPermission('scheme-redemption', 'create');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Search
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  // Selected customer and their completed enrollments
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [completedEnrollments, setCompletedEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);

  // Redemption form
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [redemptionType, setRedemptionType] = useState('full'); // full or partial
  const [redemptionAmount, setRedemptionAmount] = useState('');
  const [redemptionDate, setRedemptionDate] = useState(todayCentral());
  const [notes, setNotes] = useState('');
  const [processingBilling, setProcessingBilling] = useState(false);

  // Submission
  const [submitting, setSubmitting] = useState(false);

  // Search customers
  const searchCustomers = async (term) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      setSearching(true);
      const response = await fetch(`/api/customers/search?term=${encodeURIComponent(term)}&limit=10`);
      const result = await response.json();
      
      if (result.success) {
        setSearchResults(result.data);
      }
    } catch (err) {
      setError('Error searching customers: ' + err.message);
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (value) => {
    setSearchTerm(value);
    searchCustomers(value);
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSearchTerm('');
    setSearchResults([]);

    // Fetch their completed enrollments
    await fetchCompletedEnrollments(customer.id);
  };

  const fetchCompletedEnrollments = async (customerId) => {
    try {
      setEnrollmentsLoading(true);
      const response = await fetch(`/api/schemes/enrollments/customer/${customerId}`);
      const result = await response.json();
      
      if (result.success) {
        // Filter for enrollments that are completed or matured (all payments done)
        const completed = result.data.filter(e => {
          // Check if enrollment is matured (maturity_date passed) or completed status
          const today = new Date();
          const maturityDate = new Date(e.maturity_date);
          return e.enrollment_status === 'completed' || 
                 (e.enrollment_status === 'active' && maturityDate <= today);
        });
        setCompletedEnrollments(completed);
      } else {
        setError('Failed to fetch enrollments');
      }
    } catch (err) {
      setError('Error fetching enrollments: ' + err.message);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const selectEnrollment = (enrollment) => {
    setSelectedEnrollment(enrollment);
    // Set default redemption amount
    setRedemptionAmount(enrollment.total_amount.toString());
  };

  const handleSubmitRedemption = async (e) => {
    e.preventDefault();

    if (!canRedeem) {
      setError('You do not have permission to process redemptions');
      return;
    }

    if (!selectedCustomer || !selectedEnrollment) {
      setError('Please select a customer and enrollment');
      return;
    }

    if (!redemptionAmount) {
      setError('Please enter a redemption amount');
      return;
    }

    const amount = parseFloat(redemptionAmount);
    if (isNaN(amount) || amount <= 0) {
      setError('Redemption amount must be greater than 0');
      return;
    }

    if (redemptionType === 'partial' && amount > selectedEnrollment.total_amount) {
      setError('Partial redemption amount cannot exceed total scheme amount');
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const redemptionData = {
        enrollment_id: selectedEnrollment.id,
        customer_id: selectedCustomer.id,
        redemption_type: redemptionType,
        redemption_amount: amount,
        redemption_date: redemptionDate,
        notes,
        create_billing: processingBilling
      };

      const response = await fetch('/api/schemes/redemptions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(redemptionData)
      });

      const result = await response.json();

      if (result.success) {
        setSuccess(`Redemption processed successfully!${processingBilling ? ' Billing entry created.' : ''}`);
        
        // Reset form
        setSelectedEnrollment(null);
        setRedemptionAmount('');
        setNotes('');
        setProcessingBilling(false);
        setRedemptionType('full');
        
        // Refresh completed enrollments
        if (selectedCustomer) {
          await fetchCompletedEnrollments(selectedCustomer.id);
        }
        
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(result.error || 'Failed to process redemption');
      }
    } catch (err) {
      setError('Error processing redemption: ' + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Removed unused calculateGoldEquivalent function

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <PageIdentifier pageId={SCREEN_IDS.SCHEMES.REDEMPTION} pageName="Redemptions" />
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Scheme Redemption</h1>
        <p className="text-gray-500 mt-1">Process matured scheme redemptions and create gold billing</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={() => setError(null)} className="ml-auto">
            <X size={16} />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 flex items-center gap-2">
          <CheckCircle size={20} />
          <span>{success}</span>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: Customer Search */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100">
            <h2 className="font-bold text-gray-800 mb-4 text-lg">1. Find Customer</h2>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Search for customer
              </label>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search by name or phone..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
              />
            </div>

            {searching && <p className="text-gray-600 text-sm">Searching...</p>}

            {searchResults.length > 0 && (
              <div className="space-y-2">
                {searchResults.map(customer => (
                  <button
                    key={customer.id}
                    onClick={() => selectCustomer(customer)}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${
                      selectedCustomer?.id === customer.id
                        ? 'border-amber-500 bg-amber-50'
                        : 'border-gray-200 bg-white hover:border-amber-300'
                    }`}
                  >
                    <p className="font-semibold text-gray-800 text-sm">{customer.name}</p>
                    <p className="text-xs text-gray-500">{customer.phone}</p>
                  </button>
                ))}
              </div>
            )}

            {selectedCustomer && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-xs text-green-600 font-semibold">✓ Selected</p>
                <p className="text-sm font-semibold text-gray-800">{selectedCustomer.name}</p>
              </div>
            )}
          </div>

          {/* Completed Enrollments */}
          {selectedCustomer && (
            <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100 mt-6">
              <h2 className="font-bold text-gray-800 mb-4 text-lg">2. Select Enrollment</h2>
              
              {enrollmentsLoading ? (
                <p className="text-gray-600 text-sm">Loading enrollments...</p>
              ) : completedEnrollments.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-yellow-800 text-sm">
                    No completed or matured enrollments for this customer
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {completedEnrollments.map(enrollment => (
                    <button
                      key={enrollment.id}
                      onClick={() => selectEnrollment(enrollment)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition ${
                        selectedEnrollment?.id === enrollment.id
                          ? 'border-amber-500 bg-amber-50'
                          : 'border-gray-200 bg-white hover:border-amber-300'
                      }`}
                    >
                      <p className="font-semibold text-gray-800 text-sm">{enrollment.scheme_name}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {enrollment.currency === 'USD' ? '$' : '₹'}{enrollment.total_amount} total
                      </p>
                      <p className="text-xs text-amber-600 mt-1">
                        Matured: {new Date(enrollment.maturity_date).toLocaleDateString()}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right Column: Redemption Form */}
        <div className="lg:col-span-2">
          {selectedEnrollment ? (
            <form onSubmit={handleSubmitRedemption}>
              {/* Enrollment Summary */}
              <div className="bg-gradient-to-r from-amber-50 to-amber-100 rounded-lg p-6 mb-6 border border-amber-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Enrollment Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">Scheme</p>
                    <p className="font-bold text-gray-800">{selectedEnrollment.scheme_name}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Monthly Amount</p>
                    <p className="font-bold text-amber-600">
                      {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.monthly_amount}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Duration</p>
                    <p className="font-bold">{selectedEnrollment.duration_months} months</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Total Amount</p>
                    <p className="font-bold text-green-600">
                      {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.total_amount}
                    </p>
                  </div>
                  <div>
                    <p className="text-gray-600">Maturity Date</p>
                    <p className="font-bold">{new Date(selectedEnrollment.maturity_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Status</p>
                    <p className="font-bold text-green-600">
                      <CheckCircle className="inline mr-1" size={14} />
                      Matured
                    </p>
                  </div>
                </div>
              </div>

              {/* Redemption Type */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100 mb-6">
                <h3 className="font-bold text-gray-800 mb-4">3. Select Redemption Type</h3>
                
                <div className="space-y-3">
                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition"
                    style={{
                      borderColor: redemptionType === 'full' ? '#f59e0b' : '#e5e7eb',
                      backgroundColor: redemptionType === 'full' ? '#fef3c7' : '#ffffff'
                    }}>
                    <input
                      type="radio"
                      name="redemptionType"
                      value="full"
                      checked={redemptionType === 'full'}
                      onChange={(e) => setRedemptionType(e.target.value)}
                      className="w-4 h-4 text-amber-600"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-semibold text-gray-800">Full Redemption</p>
                      <p className="text-sm text-gray-600">
                        Redeem entire {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.total_amount}
                      </p>
                    </div>
                  </label>

                  <label className="flex items-center p-4 border-2 rounded-lg cursor-pointer transition"
                    style={{
                      borderColor: redemptionType === 'partial' ? '#f59e0b' : '#e5e7eb',
                      backgroundColor: redemptionType === 'partial' ? '#fef3c7' : '#ffffff'
                    }}>
                    <input
                      type="radio"
                      name="redemptionType"
                      value="partial"
                      checked={redemptionType === 'partial'}
                      onChange={(e) => setRedemptionType(e.target.value)}
                      className="w-4 h-4 text-amber-600"
                    />
                    <div className="ml-3 flex-1">
                      <p className="font-semibold text-gray-800">Partial Redemption</p>
                      <p className="text-sm text-gray-600">
                        Redeem a portion of the scheme amount
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              {/* Redemption Amount & Date */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100 mb-6">
                <h3 className="font-bold text-gray-800 mb-4">4. Redemption Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redemption Amount ({selectedEnrollment.currency}) *
                    </label>
                    <input
                      type="number"
                      value={redemptionAmount}
                      onChange={(e) => setRedemptionAmount(e.target.value)}
                      step="0.01"
                      min="0"
                      max={redemptionType === 'partial' ? selectedEnrollment.total_amount : undefined}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                    {redemptionType === 'partial' && (
                      <p className="text-xs text-gray-500 mt-1">
                        Max: {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.total_amount}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Redemption Date *
                    </label>
                    <input
                      type="date"
                      value={redemptionDate}
                      onChange={(e) => setRedemptionDate(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add any additional notes about this redemption..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Billing Integration */}
              <div className="bg-white rounded-lg shadow-lg p-6 border border-amber-100 mb-6">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <FileText size={20} /> Billing Integration
                </h3>
                
                <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:border-amber-300 transition">
                  <input
                    type="checkbox"
                    checked={processingBilling}
                    onChange={(e) => setProcessingBilling(e.target.checked)}
                    className="w-4 h-4 text-amber-600 rounded"
                  />
                  <div className="ml-3">
                    <p className="font-semibold text-gray-800">Create Billing Entry</p>
                    <p className="text-sm text-gray-600">
                      Automatically create a billing entry for {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{redemptionAmount || selectedEnrollment.total_amount}
                    </p>
                  </div>
                </label>

                {processingBilling && (
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm text-blue-800">
                      <strong>ℹ️ Note:</strong> A billing entry will be created with the redemption amount. 
                      You can adjust the gold content and other details in the billing section.
                    </p>
                  </div>
                )}
              </div>

              {/* Redemption Summary */}
              {redemptionAmount && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                  <p className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                    <Gift size={20} /> Redemption Summary
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Scheme Total Amount:</span>
                      <span className="font-semibold">
                        {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{selectedEnrollment.total_amount}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Redemption Amount:</span>
                      <span className="font-semibold text-green-600">
                        {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{parseFloat(redemptionAmount).toFixed(2)}
                      </span>
                    </div>
                    {redemptionType === 'partial' && (
                      <div className="flex justify-between border-t border-green-200 pt-2 mt-2">
                        <span className="text-gray-700">Remaining Balance:</span>
                        <span className="font-semibold text-amber-600">
                          {selectedEnrollment.currency === 'USD' ? '$' : '₹'}{(selectedEnrollment.total_amount - parseFloat(redemptionAmount)).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Submit Button */}
              {canRedeem ? (
                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-bold transition flex items-center justify-center gap-2 text-lg"
                >
                  <Zap size={20} /> {submitting ? 'Processing Redemption...' : 'Process Redemption'}
                </button>
              ) : (
                <p className="text-center text-sm text-gray-500 italic">You do not have permission to process redemptions.</p>
              )}
            </form>
          ) : (
            <div className="bg-white rounded-lg shadow-lg p-12 text-center border border-amber-100">
              <Gift size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">Select an enrollment to process redemption</p>
              <p className="text-gray-500 mt-2">Only matured or completed enrollments are available for redemption</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SchemeRedemption;
